import React, { useState, useEffect } from 'react';
import { adminReferralAPI } from '../../lib/api';
import { Users, Check, DollarSign, TrendingUp, UserCheck, Clock } from 'lucide-react';
import { toast } from 'sonner';

const AdminReferralListPage = () => {
  const [overview, setOverview] = useState({});
  const [referrals, setReferrals] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [totalRef, setTotalRef] = useState(0);
  const [totalCom, setTotalCom] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('referrals');
  const [comFilter, setComFilter] = useState('');
  const [approving, setApproving] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [ovRes, refRes, comRes] = await Promise.all([
        adminReferralAPI.getOverview(),
        adminReferralAPI.getReferrals(1, 50),
        adminReferralAPI.getCommissions(1, 50),
      ]);
      setOverview(ovRes.data);
      setReferrals(refRes.data.referrals || []);
      setTotalRef(refRes.data.total || 0);
      setCommissions(comRes.data.commissions || []);
      setTotalCom(comRes.data.total || 0);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const fetchCommissions = async (status) => {
    try {
      const res = await adminReferralAPI.getCommissions(1, 50, status || null);
      setCommissions(res.data.commissions || []);
      setTotalCom(res.data.total || 0);
    } catch { toast.error('Failed to filter commissions'); }
  };

  const handleApprove = async (id) => {
    setApproving(id);
    try {
      await adminReferralAPI.approveCommission(id);
      toast.success('Commission approved!');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to approve'); }
    finally { setApproving(''); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin w-8 h-8 border-2 border-[#1A73E8] border-t-transparent rounded-full" />
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-in-up">
      <div>
        <h1 className="text-2xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>Referrals & Commissions</h1>
        <p className="text-[#5F6368]">Track all referrals and manage commission approvals</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-referral-stats">
        <div className="dashboard-card flex items-center gap-3">
          <div className="w-10 h-10 bg-[#E8F0FE] rounded-lg flex items-center justify-center"><Users className="w-5 h-5 text-[#1A73E8]" /></div>
          <div><p className="text-xs text-[#5F6368]">Total Referrals</p><p className="text-xl font-semibold">{overview.total_referrals || 0}</p></div>
        </div>
        <div className="dashboard-card flex items-center gap-3">
          <div className="w-10 h-10 bg-[#E6F4EA] rounded-lg flex items-center justify-center"><UserCheck className="w-5 h-5 text-[#1E8E3E]" /></div>
          <div><p className="text-xs text-[#5F6368]">Active (Converted)</p><p className="text-xl font-semibold">{overview.active_referrals || 0}</p></div>
        </div>
        <div className="dashboard-card flex items-center gap-3">
          <div className="w-10 h-10 bg-[#FEF7E0] rounded-lg flex items-center justify-center"><Clock className="w-5 h-5 text-[#F9AB00]" /></div>
          <div><p className="text-xs text-[#5F6368]">Pending Commissions</p><p className="text-xl font-semibold">{overview.pending_commissions?.toFixed?.(2) || 0}</p></div>
        </div>
        <div className="dashboard-card flex items-center gap-3">
          <div className="w-10 h-10 bg-[#E8F0FE] rounded-lg flex items-center justify-center"><DollarSign className="w-5 h-5 text-[#1A73E8]" /></div>
          <div><p className="text-xs text-[#5F6368]">Total Paid</p><p className="text-xl font-semibold">{overview.paid_commissions?.toFixed?.(2) || 0}</p></div>
        </div>
      </div>

      {/* Tabs */}
      <div className="dashboard-card">
        <div className="flex gap-1 bg-[#F1F3F4] rounded-lg p-1 mb-4">
          {['referrals', 'commissions'].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors capitalize ${activeTab === tab ? 'bg-white text-[#202124] shadow-sm' : 'text-[#5F6368]'}`}
              data-testid={`admin-tab-${tab}`}
            >{tab}</button>
          ))}
        </div>

        {activeTab === 'referrals' && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-referrals-table">
              <thead>
                <tr className="border-b border-[#DADCE0]">
                  <th className="text-left py-2 px-3 text-xs font-medium text-[#5F6368]">Referrer</th>
                  <th className="text-left py-2 px-3 text-xs font-medium text-[#5F6368]">Referred User</th>
                  <th className="text-left py-2 px-3 text-xs font-medium text-[#5F6368]">Plan</th>
                  <th className="text-left py-2 px-3 text-xs font-medium text-[#5F6368]">Status</th>
                  <th className="text-left py-2 px-3 text-xs font-medium text-[#5F6368]">Date</th>
                </tr>
              </thead>
              <tbody>
                {referrals.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-8 text-sm text-[#5F6368]">No referrals yet</td></tr>
                ) : referrals.map(r => (
                  <tr key={r.id} className="border-b border-[#F1F3F4]">
                    <td className="py-2.5 px-3"><p className="font-medium">{r.referrer_name}</p><p className="text-xs text-[#5F6368]">{r.referrer_email}</p></td>
                    <td className="py-2.5 px-3"><p className="font-medium">{r.referred_name}</p><p className="text-xs text-[#5F6368]">{r.referred_email}</p></td>
                    <td className="py-2.5 px-3 capitalize">{r.referred_plan}</td>
                    <td className="py-2.5 px-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${r.status === 'converted' ? 'bg-[#E6F4EA] text-[#1E8E3E]' : 'bg-[#FEF7E0] text-[#F9AB00]'}`}>{r.status}</span>
                    </td>
                    <td className="py-2.5 px-3 text-[#5F6368]">{new Date(r.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'commissions' && (
          <>
            <div className="flex items-center gap-2 mb-3">
              {['', 'pending', 'approved', 'paid'].map(s => (
                <button key={s} onClick={() => { setComFilter(s); fetchCommissions(s); }}
                  className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${comFilter === s ? 'bg-[#1A73E8] text-white' : 'bg-[#F1F3F4] text-[#5F6368] hover:bg-[#E8EAED]'}`}
                  data-testid={`filter-${s || 'all'}`}
                >{s || 'All'}</button>
              ))}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-commissions-table">
                <thead>
                  <tr className="border-b border-[#DADCE0]">
                    <th className="text-left py-2 px-3 text-xs font-medium text-[#5F6368]">Referrer</th>
                    <th className="text-left py-2 px-3 text-xs font-medium text-[#5F6368]">Type</th>
                    <th className="text-left py-2 px-3 text-xs font-medium text-[#5F6368]">Plan Amt</th>
                    <th className="text-left py-2 px-3 text-xs font-medium text-[#5F6368]">Commission</th>
                    <th className="text-left py-2 px-3 text-xs font-medium text-[#5F6368]">Status</th>
                    <th className="text-left py-2 px-3 text-xs font-medium text-[#5F6368]">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {commissions.length === 0 ? (
                    <tr><td colSpan={6} className="text-center py-8 text-sm text-[#5F6368]">No commissions found</td></tr>
                  ) : commissions.map(c => (
                    <tr key={c.id} className="border-b border-[#F1F3F4]">
                      <td className="py-2.5 px-3"><p className="font-medium">{c.referrer_name}</p><p className="text-xs text-[#5F6368]">{c.referrer_email}</p></td>
                      <td className="py-2.5 px-3 capitalize text-xs">{c.commission_type?.replace('_', ' ')}</td>
                      <td className="py-2.5 px-3">{c.currency === 'INR' ? '₹' : '$'}{c.plan_amount?.toFixed(2)}</td>
                      <td className="py-2.5 px-3 font-medium text-[#1E8E3E]">{c.currency === 'INR' ? '₹' : '$'}{c.commission_amount?.toFixed(2)} ({c.commission_percent}%)</td>
                      <td className="py-2.5 px-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          c.status === 'paid' ? 'bg-[#E8F0FE] text-[#1A73E8]' : c.status === 'approved' ? 'bg-[#E6F4EA] text-[#1E8E3E]' : 'bg-[#FEF7E0] text-[#F9AB00]'
                        }`}>{c.status}</span>
                      </td>
                      <td className="py-2.5 px-3">
                        {c.status === 'pending' && (
                          <button onClick={() => handleApprove(c.id)} disabled={approving === c.id}
                            className="text-xs px-3 py-1.5 bg-[#1E8E3E] text-white rounded-lg hover:bg-[#137333] transition-colors disabled:opacity-50 flex items-center gap-1"
                            data-testid={`approve-${c.id}`}
                          >
                            {approving === c.id ? '...' : <><Check className="w-3 h-3" /> Approve</>}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminReferralListPage;
